import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

export interface PaymentHistoryItem {
  id: string;
  amount: number;
  currency: string;
  status: string;
  processed: boolean;
  description?: string;
  createdAt: string;
  updatedAt: string;
}

interface Props {
  items: PaymentHistoryItem[];
  isLoading?: boolean;
}

const formatDateTime = (value: string) => {
  try {
    return new Intl.DateTimeFormat("en-US", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(value));
  } catch {
    return value;
  }
};

const statusColors: Record<string, string> = {
  succeeded: "bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500/20",
  pending: "bg-amber-500/10 text-amber-500 hover:bg-amber-500/20",
  canceled: "bg-red-500/10 text-red-500 hover:bg-red-500/20",
  waiting_for_capture: "bg-amber-500/10 text-amber-500 hover:bg-amber-500/20"
};

const PaymentHistory: React.FC<Props> = ({ items, isLoading }) => {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <div className="space-y-1.5">
          <CardTitle>Payment History</CardTitle>
          <CardDescription>Recent top-ups are displayed here.</CardDescription>
        </div>
        {isLoading && <span className="text-xs text-muted-foreground">Updating...</span>}
      </CardHeader>
      <CardContent>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ID</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Updated</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">
                    No payments yet. Top up your balance to see history.
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="font-mono text-xs text-muted-foreground">{item.id}</TableCell>
                    <TableCell className="font-semibold">
                      {item.amount.toLocaleString("en-US", {
                        style: "currency",
                        currency: item.currency === 'RUB' ? 'USD' : item.currency, // Force USD or handle conversion if needed
                        maximumFractionDigits: 2,
                      })}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className={statusColors[item.status]}>
                        {item.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {item.description ?? "—"}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{formatDateTime(item.createdAt)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{formatDateTime(item.updatedAt)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

export default PaymentHistory;
